import { type JSX, type ReactNode } from 'react';

import { chakra, defineRecipe, EloHeaderPage } from '@plurall/elo-v3';

import { SchoolSelector } from '@/components/SchoolSelector';

// EloHeaderPage is the container (containerType="inline-size" passed via props spread).
// Below viewport md: EloHeaderPage switches to column layout (flexDirection from "row" to "column"),
// so children take full width. Above md: children sit beside the title, capped at 40cqi.
// A viewport md: condition is used deliberately here because EloHeaderPage's own flex direction
// switch is viewport-based — a container query alone can't match it due to sidebar width variance.
const ControlsFactory = chakra(
  'div',
  defineRecipe({
    className: 'page-header-controls',
    base: {
      display: 'flex',
      alignItems: 'center',
      gap: 'gap.small',
      '& input': {
        fieldSizing: 'content',
        maxInlineSize: '40cqi',
      },
      md: {
        '& input': {
          minInlineSize: '30ch',
        },
      },
      mdDown: {
        // 100% is relative to Elo's input wrapper, not the container — use cqi instead.
        // 66px = 2 × (icon 24px + padding 0.5rem) + ~2px border chrome from EloInputSearch
        '--input-chrome-width': '66px',
        '& input': {
          maxInlineSize: 'calc(100cqi - var(--input-chrome-width))',
        },
      },
    },
  })
);

type Props = {
  title: string;
  description?: string;
  onBack?: () => void;
  /** Show the school selector in the controls area. Defaults to true. */
  showSchoolSelector?: boolean;
  /** Content for EloHeaderPage.TitleSlot — use for badges, tooltips, or other title decorations. */
  titleSlot?: ReactNode;
  /** Additional controls rendered alongside the school selector. */
  children?: ReactNode;
};

export function PageHeader({
  title,
  description,
  onBack,
  showSchoolSelector = true,
  titleSlot,
  children,
}: Props): JSX.Element {
  const hasControls = showSchoolSelector || !!children;

  return (
    <EloHeaderPage.Root
      title={title}
      description={description}
      containerType='inline-size'>
      {onBack && <EloHeaderPage.TextLink onClick={onBack}>Voltar</EloHeaderPage.TextLink>}
      {titleSlot && <EloHeaderPage.TitleSlot>{titleSlot}</EloHeaderPage.TitleSlot>}
      {hasControls && (
        <ControlsFactory>
          {showSchoolSelector && <SchoolSelector />}
          {children}
        </ControlsFactory>
      )}
    </EloHeaderPage.Root>
  );
}
